import { useCallback, useEffect, useState } from "react";
import { Inbox, Loader2, LogOut, Check, Trash2, Undo2, Heart, Eye, EyeOff, Languages, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { FadeUp } from "@/components/Reveal";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "nlc_admin_token";

const inputCls =
  "w-full rounded-xl border border-border bg-linen px-4 py-3 text-sm text-espresso placeholder:text-taupe/70 transition-colors focus:border-terracotta focus:outline-none";

const StoryRow = ({ s, isPublished, a, catLabel, busyId, act, translate, saveEdit }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ en: "", es: "" });

  const startEdit = () => {
    setDraft({ en: s.translations?.en || "", es: s.translations?.es || "" });
    setEditing(true);
  };

  const handleSave = async () => {
    const ok = await saveEdit(s.id, draft);
    if (ok) setEditing(false);
  };

  return (
    <div
      data-testid={`story-row-${s.id}`}
      className="rounded-2xl bg-white border border-border p-6 flex flex-col gap-4"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {s.photo_path ? (
            <img
              src={`${API}/files/${s.photo_path}`}
              alt={s.name}
              data-testid={`story-photo-${s.id}`}
              className="w-10 h-10 rounded-full object-cover bg-cream shrink-0"
            />
          ) : (
            <span className="w-10 h-10 rounded-full bg-terracotta-soft text-terracotta flex items-center justify-center text-sm font-bold shrink-0">
              {s.name.trim()[0]?.toUpperCase()}
            </span>
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{s.name}</p>
            <p className="text-xs text-taupe">
              {catLabel(s)} · {(s.created_at || "").slice(0, 10)}
              {s.email ? ` · ${s.email}` : ""}
            </p>
          </div>
        </div>
        <span
          className={`shrink-0 text-[10px] uppercase tracking-[0.15em] font-bold rounded-full px-3 py-1 ${
            isPublished ? "bg-sage-soft text-sage-dark" : "bg-sand text-terracotta-dark"
          }`}
        >
          {isPublished ? a.published : a.pending}
        </span>
      </div>

      {s.translations ? (
        editing ? (
          <div className="space-y-3" data-testid={`edit-box-${s.id}`}>
            {[
              ["en", a.versionEn],
              ["es", a.versionEs],
            ].map(([l, label]) => (
              <div key={l}>
                <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-taupe mb-1">
                  {label}
                </label>
                <textarea
                  data-testid={`edit-textarea-${l}-${s.id}`}
                  rows={3}
                  maxLength={2000}
                  value={draft[l]}
                  onChange={(e) => setDraft({ ...draft, [l]: e.target.value })}
                  className={`${inputCls} resize-none`}
                />
              </div>
            ))}
            <div className="flex items-center gap-3">
              <button
                data-testid={`edit-save-${s.id}`}
                onClick={handleSave}
                disabled={busyId === `e-${s.id}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-espresso text-linen px-4 py-2 text-xs font-semibold hover:bg-night transition-colors disabled:opacity-60"
              >
                {busyId === `e-${s.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                {busyId === `e-${s.id}` ? a.saving : a.save}
              </button>
              <button
                data-testid={`edit-cancel-${s.id}`}
                onClick={() => setEditing(false)}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-taupe hover:text-espresso transition-colors"
              >
                <X className="w-3.5 h-3.5" /> {a.cancel}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid={`translations-${s.id}`}>
            {[
              ["en", a.versionEn],
              ["es", a.versionEs],
            ].map(([l, label]) =>
              s.translations[l] ? (
                <div key={l} className="rounded-xl bg-cream/70 border border-border px-4 py-3">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-taupe mb-1">{label}</p>
                  <p className="text-sm text-clay leading-relaxed">&ldquo;{s.translations[l]}&rdquo;</p>
                </div>
              ) : null
            )}
            <button
              data-testid={`edit-btn-${s.id}`}
              onClick={startEdit}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-taupe hover:text-espresso transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" /> {a.edit}
            </button>
          </div>
        )
      ) : (
        <p className="text-sm text-clay leading-relaxed">&ldquo;{s.message}&rdquo;</p>
      )}

      <div className="flex flex-wrap items-center gap-3 pt-1">
        {isPublished ? (
          <button
            data-testid={`unpublish-btn-${s.id}`}
            onClick={() => act(s.id, "unpublish")}
            disabled={busyId === s.id}
            className="inline-flex items-center gap-1.5 rounded-full bg-cream border border-border px-4 py-2 text-xs font-semibold hover:bg-sand transition-colors disabled:opacity-60"
          >
            <Undo2 className="w-3.5 h-3.5" /> {a.unpublish}
          </button>
        ) : (
          <button
            data-testid={`publish-btn-${s.id}`}
            onClick={() => act(s.id, "publish")}
            disabled={busyId === s.id}
            className="inline-flex items-center gap-1.5 rounded-full bg-terracotta text-linen px-4 py-2 text-xs font-semibold hover:bg-terracotta-dark transition-colors disabled:opacity-60"
          >
            {busyId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {a.publish}
          </button>
        )}
        {!s.translations && (
          <button
            data-testid={`translate-btn-${s.id}`}
            onClick={() => translate(s.id)}
            disabled={busyId === `t-${s.id}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-sage-soft text-sage-dark px-4 py-2 text-xs font-semibold hover:bg-sage hover:text-linen transition-colors disabled:opacity-60"
          >
            {busyId === `t-${s.id}` ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Languages className="w-3.5 h-3.5" />
            )}
            {busyId === `t-${s.id}` ? a.translating : a.translate}
          </button>
        )}
        <button
          data-testid={`discard-btn-${s.id}`}
          onClick={() => act(s.id, "discard")}
          disabled={busyId === s.id}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-taupe hover:text-red-600 transition-colors disabled:opacity-60"
        >
          <Trash2 className="w-3.5 h-3.5" /> {a.discard}
        </button>
      </div>
    </div>
  );
};

export default function Admin() {
  const { t } = useLang();
  const a = t.admin;
  const tp = t.testimonialsPage;
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [stories, setStories] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setStories(null);
  }, []);

  const load = useCallback(
    async (tk) => {
      try {
        const res = await fetch(`${API}/testimonials/submissions`, {
          headers: { Authorization: `Bearer ${tk}` },
        });
        if (res.status === 401) {
          logout();
          return;
        }
        if (!res.ok) throw new Error();
        const data = await res.json();
        setStories(data.stories || []);
      } catch {
        toast.error(a.actionError);
      }
    },
    [a.actionError, logout]
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setSigningIn(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(typeof data.detail === "string" ? data.detail : a.loginError);
        return;
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch {
      toast.error(a.loginError);
    } finally {
      setSigningIn(false);
    }
  };

  const act = async (id, action) => {
    setBusyId(id);
    try {
      const res = await fetch(`${API}/testimonials/${id}${action === "discard" ? "" : `/${action}`}`, {
        method: action === "discard" ? "DELETE" : "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      await load(token);
    } catch {
      toast.error(a.actionError);
    } finally {
      setBusyId(null);
    }
  };

  const translate = async (id) => {
    setBusyId(`t-${id}`);
    try {
      const res = await fetch(`${API}/testimonials/${id}/translate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      await load(token);
    } catch {
      toast.error(a.actionError);
    } finally {
      setBusyId(null);
    }
  };

  const saveEdit = async (id, translations) => {
    setBusyId(`e-${id}`);
    try {
      const res = await fetch(`${API}/testimonials/${id}/edit`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ translations }),
      });
      if (!res.ok) throw new Error();
      await load(token);
      return true;
    } catch {
      toast.error(a.actionError);
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const catLabel = (s) => (s.custom_category || "").trim() || (s.category === "other" ? tp.otherLabel : tp.filters[s.category] || s.category);

  if (!token) {
    return (
      <section data-testid="admin-login-section" className="pt-40 pb-28 max-w-md mx-auto px-4 sm:px-8">
        <FadeUp>
          <div className="rounded-3xl bg-white border border-border p-8 sm:p-10 shadow-[0_16px_40px_-24px_rgba(44,37,32,0.2)]">
            <span className="w-14 h-14 rounded-2xl bg-terracotta-soft flex items-center justify-center mb-6">
              <Heart className="w-6 h-6 text-terracotta" />
            </span>
            <h1 className="font-serif text-3xl font-semibold" data-testid="admin-login-title">
              {a.signInTitle}
            </h1>
            <p className="mt-2 text-sm text-clay">{a.subtitle}</p>
            <form onSubmit={login} className="mt-7 space-y-5" data-testid="admin-login-form">
              <div>
                <label htmlFor="admin-email" className="block text-xs font-semibold uppercase tracking-[0.15em] text-taupe mb-2">
                  {a.emailLabel}
                </label>
                <input
                  id="admin-email"
                  data-testid="admin-email-input"
                  type="email"
                  required
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="username"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="admin-password" className="block text-xs font-semibold uppercase tracking-[0.15em] text-taupe mb-2">
                  {a.passwordLabel}
                </label>
                <div className="relative">
                  <input
                    id="admin-password"
                    data-testid="admin-password-input"
                    type={showPassword ? "text" : "password"}
                    required
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`${inputCls} pr-12`}
                  />
                  <button
                    type="button"
                    data-testid="admin-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-taupe hover:text-espresso transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={signingIn}
                data-testid="admin-login-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-terracotta text-linen px-8 py-4 text-base font-semibold transition-[background-color,transform] duration-200 hover:bg-terracotta-dark [@media(hover:hover)]:hover:-translate-y-0.5 disabled:opacity-60"
              >
                {signingIn && <Loader2 className="w-5 h-5 animate-spin" />}
                {signingIn ? a.signingIn : a.signIn}
              </button>
            </form>
          </div>
        </FadeUp>
      </section>
    );
  }

  const pending = (stories || []).filter((s) => s.status !== "published");
  const published = (stories || []).filter((s) => s.status === "published");

  return (
    <section data-testid="admin-inbox" className="pt-32 sm:pt-40 pb-24 max-w-3xl mx-auto px-4 sm:px-8">
      <FadeUp>
        <div className="flex items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-terracotta mb-3">
              <Inbox className="inline w-4 h-4 mr-1.5 -mt-0.5" />
              {t.nav.testimonials}
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold" data-testid="admin-inbox-title">
              {a.title}
            </h1>
            <p className="mt-3 text-sm sm:text-base text-clay">{a.subtitle}</p>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-taupe hover:text-espresso transition-colors"
          >
            <LogOut className="w-4 h-4" /> {a.signOut}
          </button>
        </div>

        {stories === null ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 text-terracotta animate-spin" />
          </div>
        ) : (
          <div className="space-y-12">
            <div>
              <h2 className="text-xs uppercase tracking-[0.25em] font-semibold text-taupe mb-5">
                {a.pending} · {pending.length}
              </h2>
              {pending.length === 0 ? (
                <p className="text-sm text-taupe rounded-2xl bg-cream border border-border px-6 py-5" data-testid="admin-empty-pending">
                  {a.emptyPending}
                </p>
              ) : (
                <div className="space-y-4">
                  {pending.map((s) => (
                    <StoryRow
                      key={s.id}
                      s={s}
                      isPublished={false}
                      a={a}
                      catLabel={catLabel}
                      busyId={busyId}
                      act={act}
                      translate={translate}
                      saveEdit={saveEdit}
                    />
                  ))}
                </div>
              )}
            </div>
            <div>
              <h2 className="text-xs uppercase tracking-[0.25em] font-semibold text-taupe mb-5">
                {a.published} · {published.length}
              </h2>
              {published.length === 0 ? (
                <p className="text-sm text-taupe rounded-2xl bg-cream border border-border px-6 py-5" data-testid="admin-empty-published">
                  {a.emptyPublished}
                </p>
              ) : (
                <div className="space-y-4">
                  {published.map((s) => (
                    <StoryRow
                      key={s.id}
                      s={s}
                      isPublished
                      a={a}
                      catLabel={catLabel}
                      busyId={busyId}
                      act={act}
                      translate={translate}
                      saveEdit={saveEdit}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </FadeUp>
    </section>
  );
}
